import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { InputDataTaskDispatchProcess, InputDataTaskForm, InputDataTaskForward, OutputDataTaskDispatchProcess } from '../../../models/input-output-data.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { Papel, Processo } from '../../../models/edocs.model';
import { Utils } from '../../../shared/utils';
import { FlowDefinition, FlowInstance, FlowInstanceState } from '../../../models/flow.model';
import { CookieService } from 'ngx-cookie-service';
import { ConfigSchema, ConfigSchemaSectionEDocsLocation } from '../../../models/config-schema.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-dispatch-process',
    templateUrl: './fob-dispatch-process.component.html',
    styleUrl: './fob-dispatch-process.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectDispatchProcessComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskDispatchProcess = null;
    forwardingProtocol = Enums.Messages.DefaultEmptyValue as string;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskDispatchProcess);
    $configSchema = signal(null as ConfigSchema);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $papeisSelector = signal([] as Papel[]);
    $processProtocol = signal(Enums.Messages.DefaultEmptyValue as string);
    $publicMessageHtml = signal(null as string);
    $process = signal(null as Processo);
    $dispatcher = signal('');
    $recipient = signal(null as ConfigSchemaSectionEDocsLocation);
    $custodyHolder = signal(Enums.Messages.DefaultEmptyValue as string);
    $canDispatchProcess = signal(false);
    $isProcessInvalidStatus = signal(false);
    $isDispatchRecipientInvalid = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $dateString = computed(() => this.$model() == null ? '' : new Date(this.$model().updateDate).toLocaleString());
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    @Output() _cancelFlowInstanceEvent = new EventEmitter<boolean>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private cookieService: CookieService,
        private authService: AuthService,
        private eDocsService: EDocsService
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$configSchema, JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema);
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskDispatchProcess(), JSON.parse(this.$model().inputData)));
        this.outputData = Utils.isNullOrEmpty(this.$model().outputData) ? null : Object.assign(new OutputDataTaskDispatchProcess(), JSON.parse(this.$model().outputData));

        // #region [protocolo de Encaminhamento]
        this.forwardingProtocol = this.$flowInstance().rootForwardingInfo;
        // #endregion

        // #region [protocolo de Processo]
        if (Utils.isNotNullOrEmpty(this.$inputData().eDocsData.processId)) {
            const response = await this.eDocsService.getProcesso(this.$inputData().eDocsData.processId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            Utils.setSignal(this.$process, response.data);
            this.$processProtocol.set(this.$process().protocolo);

            if (
                this.$process().situacao != Enums.ProcessStatus.EmAndamento.toString()
                && this.$process().situacao != Enums.ProcessStatusLabel.EmAndamento
            ) {
                this.$canDispatchProcess.set(false);
                this.$isProcessInvalidStatus.set(true);
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            } else {
                this.$canDispatchProcess.set(true);
            }

            // resolve o nome do detentor atual da custódia do Processo
            const response_LocalCustodia = await this.eDocsService.getProcessoLocalCustodia(this.$inputData().eDocsData.processId);

            if (!response_LocalCustodia.isSuccess) {
                this.toastr.error(response_LocalCustodia.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            const localCustodia = response_LocalCustodia.data;
            this.$custodyHolder.set(localCustodia.nome);
        }
        // #endregion

        // #region [resumo do Processo]
        this.resolveDispatchSummary();
        // #endregion

        // #region [remetente do Processo]
        let lastForwardingTask = this.$flowInstance().flowObjectInstances
            .filter(x => x.flowObjectDefinition.typeId == FlowObjectType.TaskForward)
            .sort((a, b) => new Date(b.createDate).toISOString().localeCompare(new Date(a.createDate).toISOString()))[0];
        this.$dispatcher.set((JSON.parse(lastForwardingTask.inputData) as InputDataTaskForward).recipientName);
        // #endregion

        // #region [destinatário do Processo]
        let recipient = this.$configSchema()?.taskDispatchProcess?.dispatchRecipient;
        this.$recipient.set(recipient);

        const response = await this.eDocsService.getAgente(this.$recipient().id);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        let agent = response.data;
        if (!agent.ativo) {
            this.$isDispatchRecipientInvalid.set(true);
            this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
        }
        // #endregion

        // #region [seletor de papéis]
        if (this.$shouldLetPublicAgentInteract()) {
            Utils.setSignal(this.$papeisSelector, this.$currentUser().papeis);

            // força o usuário a selecionar um papel manualmente
            setTimeout(() => Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = null;
                return x;
            }), 50);
        }

        if (this.$papeisSelector().length == 0) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.forwardingProtocol,
                this.$processProtocol()
            )), 1000);
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    goToProcess() {
        const resolvedSubdomain = Utils.getResolvedEDocsSubdomain(this.cookieService);
        window.open(`https://${resolvedSubdomain}es.gov.br/p/${this.$processProtocol()}`, '_blank');
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    submit() {
        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    getDispatchSummary() {
        return this.$configSchema()?.taskDispatchProcess?.dispatchSummary?.replaceAll('\n', '<br>');
    }

    // #region [===== private methods =====]
    // #endregion

    private getFormData(): any {
        let formTaskDefinition = this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formTaskDefinition == null) return null;

        let formTaskInstance = this.$flowInstance().flowObjectInstances.find(x => x.flowObjectDefinitionId == formTaskDefinition.id);
        let formData = JSON.parse(formTaskInstance.inputData) as InputDataTaskForm;

        return formData.data;
    }

    private resolveDispatchSummary() {
        let formData = this.getFormData();
        if (formData == null) return;

        let tags = this.$configSchema().taskDispatchProcess.dispatchSummary.match(/\{\|([\w]+)\|\}/g);
        if (tags?.length > 0) {
            let formFields = tags.map(x => x.replace(/[^\w]/g, ''));

            for (let i = 0; i < tags.length; i++) {
                Utils.updSignal(this.$configSchema, x => {
                    x.taskDispatchProcess.dispatchSummary = x.taskDispatchProcess.dispatchSummary.replaceAll(
                        tags[i],
                        formData[formFields[i]] || Enums.Messages.DefaultEmptyValue
                    );
                    return x;
                });
            }
        }

        if (this.$configSchema().taskDispatchProcess.dispatchSummary.match(/\{\|([\w]+)\|\}/g)?.length > 0) {
            setTimeout(() => this.resolveDispatchSummary(), 200);
        }
    }
}
